<template>
  <component :is="layoutComponent"
             class="tabs__wrapper"
             :vertical-nav-classes="verticalNavClasses"
             :vertical-tab-content-classes="verticalTabContentClasses"
             :vertical="vertical">
    <template slot="nav">
      <div v-if="styleType === 'list'" :class="tabNavClasses">
        <el-select class="select-default tab-list-select"
                   value-key="title"
                   @change="onSelectChange"
                   :value="currentActiveTab">
          <el-option v-for="tab in tabs"
                     class="select-default"
                     :key="tab.title"
                     :value="tab"
                     :label="tab.title">
          </el-option>

        </el-select>

      </div>
      <ul v-else
          class="nav"
          role="tablist"
          :class="
            [type ? `nav-pills-${type}`: '',
              pills ? 'nav-pills': 'nav-tabs',
             {'nav-pills-icons': icons},
             {'flex-column nav-stacked': vertical},
             {'justify-content-center': centered},
             tabNavClasses
            ]">

        <li v-for="tab in tabs"
            class="nav-item"
            :key="tab.id || tab.title">

          <router-link data-toggle="tab"
                       role="tab"
                       class="nav-link"
                       :to="getLink(tab)"
                       @click.native.prevent="activateTab(tab)"
                       :aria-selected="tab.active">
            <tab-item-content :tab="tab">
            </tab-item-content>
          </router-link>

        </li>

      </ul>
    </template>
    <div slot="content" class="tab-content"
         :class="[tabContentClasses]">
      <slot></slot>
    </div>
  </component>
</template>

<script>
  import PillsLayout from './PillsLayout'
  import TabsLayout from './TabsLayout'
  import { Select, Option } from 'element-ui'
  import { mapGetters } from 'vuex';

  export default {
    name: 'tabs',
    components: {
      TabsLayout,
      PillsLayout,
      TabItemContent: {
        props: ['tab'],
        render(h) {
          return h('div', [this.tab.$slots.title || this.tab.title])
        }
      },
      [Select.name]: Select,
      [Option.name]: Option
    },
    provide() {
      return {
        addTab: this.addTab,
        removeTab: this.removeTab
      }
    },
    computed: {
      layoutComponent() {
        return this.pills ? 'pills-layout' : 'tabs-layout';
      },
      ...mapGetters('utils', ['isMobile'])
    },
    props: {
      type: {
        type: String,
        default: '',
        validator: (value) => {
          let acceptedValues = ['', 'primary', 'secondary', 'info', 'success', 'warning', 'danger']
          return acceptedValues.indexOf(value) !== -1
        },
        description: 'Tabs type (primary|info|danger|default|warning|success)'
      },
      pills: {
        type: Boolean,
        description: 'Whether tabs are pills'
      },
      activeTab: {
        type: String,
        default: '',
        description: 'Default active tab name'
      },
      styleType: {
        type: String,
        default: '',
        description: 'Tabs style type (default|list)'
      },
      tabNavWrapperClasses: {
        type: [String, Object],
        default: '',
        description: 'Tab Nav wrapper (div) css classes'
      },
      tabNavClasses: {
        type: [String, Object],
        default: '',
        description: 'Tab Nav (ul) css classes'
      },
      tabContentClasses: {
        type: [String, Object],
        default: '',
        description: 'Tab content css classes'
      },
      vertical: {
        type: Boolean,
        description: 'Whether tabs are vertical'
      },
      verticalNavClasses: {
        type: [String, Object],
        default: 'col-lg-4 col-md-5 col-sm-4 col-6',
        description: 'Vertical tab css classes'
      },
      verticalTabContentClasses: {
        type: [String, Object],
        default: 'col-lg-8 col-md-7 col-sm-8 col-6',
        description: 'Vertical Tab content css classes'
      },
      icons: {
        type: Boolean,
        description: 'Whether tabs should be of icon type (small no text)'
      },
      centered: {
        type: Boolean,
        description: 'Whether tabs are centered'
      },
      value: {
        type: String,
        description: 'Initial value (active tab)'
      }
    },
    data() {
      return {
        tabs: [],
        currentActiveTab: {}
      }
    },
    methods: {
      getLink(tab) {
        let link = {name: this.$route.name, params: this.$route.params};
        if(tab.id) {
          link.hash =`#${tab.id}`
        }
        return link;
      },
      findAndActivateTab(title) {
        let tabToActivate = this.tabs.find(t => t.title === title);
        if (tabToActivate) {
          this.activateTab(tabToActivate);
        }
      },
      async activateTab(tab) {
        if (tab.beforeChange) {
          await tab.beforeChange();
        }
        if (tab.onClick) {
          tab.onClick(tab);
          return;
        }
        this.deactivateTabs();
        tab.active = true;
        this.currentActiveTab = tab;
        if (!this.$route.hash && tab.id) {
          let { name, params, query } = this.$route
          this.$router.replace({ name, params, query, hash: `#${tab.id}` })
        }
      },
      deactivateTabs() {
        this.tabs.forEach(tab => {
          tab.active = false
        });
      },
      addTab(tab) {
        const index = this.$slots.default.indexOf(tab.$vnode)
        if (!this.activeTab && index === 0) {
          tab.active = true;
          this.currentActiveTab = tab;
        }
        if (this.activeTab === tab.name) {
          tab.active = true;
          this.currentActiveTab = tab;
        }
        this.tabs.splice(index, 0, tab)
      },
      removeTab(tab) {
        const tabs = this.tabs;
        const index = tabs.indexOf(tab)
        if (index > -1) {
          tabs.splice(index, 1)
        }
      },
      async onSelectChange(tab) {
        this.currentActiveTab = tab;
        await this.activateTab(tab);
        this.$router.push({ name: this.$route.name, params: this.$route.params, hash: `#${tab.id}` })
      },
      async activateByHashId(hash) {
        let tab = this.tabs.find(t => t.id && hash.endsWith(t.id));
        if (tab) {
          await this.activateTab(tab);
        } else if(this.tabs.length) {
          await this.activateTab(this.tabs[0]);
        }
      }
    },
    mounted() {
      this.$nextTick(async () => {
        if (this.value) {
          this.findAndActivateTab(this.value);
        } else {
          await this.activateTab(this.tabs[0]);
        }
        if (this.$route.hash) {
          await this.activateByHashId(this.$route.hash);
        }
      })
    },
    watch: {
      value(newVal) {
        this.findAndActivateTab(newVal);
      },
      currentActiveTab(newVal) {
        this.$emit('tab-change', newVal);
      }
    }
  }
</script>
<style lang="scss">
  .tab-list-select.el-select {
    width: 260px;
  }
</style>

